import { memo, useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
    IconButton,
    List,
    ListItem,
    ListItemText,
    Box,
    Chip,
    useTheme,
    Link,
    Grid,
    Tooltip,
    MenuItem,
    Menu,
    Divider,
    Collapse,
    Paper,
    Typography,
    Button,
} from "@mui/material";
import { RoundArrowUp, Paperclip, Hashtag, File } from "@solar-icons/react/ssr";

import { request } from "@/Api";
import { API_BASE_URL, DEFAULT_LLM_MODEL } from "@/config";
import { selectToken } from "@/auth/authSlice";
import { setError } from "@/nav/navSlice";
import { tryParseJson } from "@/utils";

import { TextFieldBorderless } from "@/common/CommonComponents";
import { DotLoader } from "@/common/StyledComponents";
import AiLogo from "@/ai/AiLogo";
import { processMessageData } from "@/ai/AiMessageProcessor";
import MentionDropdown from "@/ai/MentionDropdown";
import ChatOptionsCard from "@/ai/ChatOptionsCard";
import { sseRequestFormData } from "@/ai/api";
import { JsonNavigator } from "@/ai/JsonNavigator";
import { MODEL_NAME_MAX_TOKEN_MAP, FILE_ACCEPT_FORMATS } from "@/ai/constants";
import {
    selectCompletion,
    selectToolCompletion,
    setCompletion,
    setToolCompletion,
} from "./aiSlice";

const HTTP_ERROR_MESSAGES = {
    413: "The files you uploaded are too large. Please reduce the number or size of files and try again.",
    429: "Too many requests. Please wait a moment and try again.",
    500: "An internal server error occurred. Please try again later.",
    502: "The server is temporarily unavailable. Please try again.",
    503: "The service is currently unavailable. Please try again later.",
};

/**
 * Extracts a human-readable error message from an Axios error.
 * Axios wraps 413 (and similar) as a generic "Network Error" when the
 * server closes the connection before the upload completes, so we
 * check both the response status and the error code.
 */
function formatSendError(error) {
    const status = error?.response?.status;
    if (status && HTTP_ERROR_MESSAGES[status]) {
        return HTTP_ERROR_MESSAGES[status];
    }

    // Axios "Network Error" often masks 413 — the server rejects the
    // payload and closes the connection before the client finishes uploading.
    if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
        return "The request failed — this is most likely because the files are too large. Please reduce the number or size of files and try again.";
    }

    // Fallback: use server message if available, else the error message
    const serverMessage =
        error?.response?.data?.detail || error?.response?.data?.message;
    if (serverMessage) return serverMessage;

    return error?.message || "An unexpected error occurred.";
}

export default function AiChatSingleThreaded({
    defaultThreadId,
    onNewThread,
    onNewMessage,
    onNewMessageSnapshot,
    onLoadThread,
    onError,
    onWaiting,
    onStopWaiting,
    onFilesAttached,

    newThreadData,
    toolSet,

    // Polymorphic thread ownership (for multi-agent)
    modelType,
    modelInstanceId,
    modelLabel,
    agentType,

    title = "New project request",
    description = null,
    suggestions = null,
    enableMentions = true,
    welcomeContent = null,
}) {
    const dispatch = useDispatch();

    const [threadId, setThreadId] = useState(defaultThreadId);
    const [messages, setMessages] = useState([]);
    const [waiting, setWaiting] = useState(false);
    const [input, setInput] = useState("");
    const [pendingFiles, setPendingFiles] = useState([]);
    const [pendingChatOptions, setPendingChatOptions] = useState(null);
    const deferredChatOptionRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);

    // Auto-populate default mention from model context (e.g., the current project)
    const defaultMentions =
        modelType && modelInstanceId && modelLabel
            ? [
                  {
                      type: modelType,
                      id: modelInstanceId,
                      label: modelLabel,
                      isDefault: true,
                  },
              ]
            : [];
    const [mentions, setMentions] = useState(defaultMentions);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [modelsAnchorEl, setModelsAnchorEl] = useState(null);
    const [selectedModel, setSelectedModel] = useState(DEFAULT_LLM_MODEL);

    // Sync threadId when the parent resolves an existing thread (e.g. after async fetch)
    useEffect(() => {
        if (defaultThreadId && defaultThreadId !== threadId) {
            setThreadId(defaultThreadId);
        }
    }, [defaultThreadId]);

    const modelsMenuOpen = Boolean(modelsAnchorEl);
    const token = useSelector(selectToken);

    const chatWindowRef = useRef(null);
    const chatWindowPinnedRef = useRef(true);
    const fileInputRef = useRef(null);
    const mentionDropdownRef = useRef(null);

    const trimmedInput = input.trim();
    const isMentionOnly =
        trimmedInput.length === 0 &&
        mentions.length > 0 &&
        pendingFiles.length === 0;
    const canSend =
        (trimmedInput || pendingFiles.length > 0) && !waiting && !isMentionOnly;

    const abortControllerRef = useRef(null);

    const setCompletionState = (value) => {
        dispatch(setCompletion(value));
    };

    const setToolCompletionState = (value) => {
        dispatch(setToolCompletion(value));
    };

    const resumeThread = (threadId) => {
        if (
            abortControllerRef.current &&
            !abortControllerRef.current.signal.aborted
        ) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        abortControllerRef.current.signal.onabort = handleAbort;
        sseRequestFormData(
            `${API_BASE_URL}/threads/${threadId}/resume`,
            null,
            token,
            handleRequestSent,
            handleReceive,
            handleError,
            handleDone,
            handleAbort,
            abortControllerRef.current.signal,
        );
    };

    const handleSend = async (
        text,
        files = [],
        promptTemplate = "",
        mentionsOverride = null,
    ) => {
        if (!text && !promptTemplate && files.length === 0) {
            return;
        }
        setPendingChatOptions(null);

        const formData = new FormData();
        formData.append("text", text || "");
        formData.append("model", selectedModel);
        formData.append("thread_id", threadId || "");
        formData.append("tool_set", toolSet || "general");
        formData.append("prompt_template", promptTemplate || "");
        if (modelType) formData.append("model_type", modelType);
        if (modelInstanceId)
            formData.append("model_instance_id", String(modelInstanceId));
        if (agentType && !threadId) formData.append("agent_type", agentType);

        // Serialize @mentions as JSON
        const mentionsToSend = mentionsOverride ?? mentions;
        if (mentionsToSend.length > 0) {
            formData.append("mentions", JSON.stringify(mentionsToSend));
        }

        files.forEach((file) => {
            formData.append("files", file);
        });

        request
            .post(`${API_BASE_URL}/llm-message`, formData)
            .then((response) => {
                const thread = response.data;
                if (thread) {
                    handleNewThread(thread);
                } else {
                    resumeThread(threadId);
                }
            })
            .catch((error) => {
                if (error.response && error.response.status === 403) {
                    dispatch(setError({ message: "Insufficient credit" }));
                } else {
                    dispatch(setError({ message: formatSendError(error) }));
                }
            });
    };

    const handleRequestSent = () => {
        startWaiting();
        setInput("");
    };

    const handleReceive = (event) => {
        const data = tryParseJson(event.data);
        if (!data) {
            return;
        }
        const eventType = event.type;
        const role = data.role;

        if (data.type == "tool_result") {
            startWaiting();
        }

        if (eventType === "thread_title_updated") {
            onNewThread && onNewThread(data.thread_id);
        } else if (eventType == "usages") {
            onNewMessage && onNewMessage(data);
        } else if (data.role && data.content) {
            const component = messageDataToComponent(data);
            addMessage(role, component);
            onNewMessage && onNewMessage(data);
            const chatOptionsData = extractChatOptions(data);
            if (chatOptionsData) {
                setPendingChatOptions(chatOptionsData);
            }
        } else if (data.snapshot) {
            if (data.type == "text") {
                setCompletionState(data.snapshot);
            } else if (data.type == "input_json") {
                setToolCompletionState(data);
            }
            onAutocompleteChange();
            onNewMessageSnapshot && onNewMessageSnapshot(data);
        } else if (data.type == "message_stop") {
            stopWaiting();
        } else if (eventType == "error") {
            dispatch(setError({ message: JSON.stringify(data) }));
        }
    };

    const handleAbort = () => {
        // TODO: implement
    };

    const startWaiting = () => {
        setWaiting(true);
        onWaiting && onWaiting();
    };

    const stopWaiting = () => {
        setWaiting(false);
        setCompletionState("");
        setToolCompletionState(null);

        // Flush any deferred chat option selection now that the stream is done
        if (deferredChatOptionRef.current) {
            const deferredText = deferredChatOptionRef.current;
            deferredChatOptionRef.current = null;
            handleSend(deferredText);
        }
        onStopWaiting && onStopWaiting();
    };

    const handleError = (error) => {
        if (error.status == 401) {
            checkToken();
        } else {
            onUnhandledError(error);
        }
    };

    const onUnhandledError = (error) => {
        onError && onError(error);
        console.error("Unhandled SSE Request Error occurred:", error);
        dispatch(setError({ message: JSON.stringify(error) }));
    };

    const handleDone = () => {
        stopWaiting();
    };

    const addMessage = (role, content) => {
        setMessages((prevMessages) => {
            return [...prevMessages, { role, content }];
        });
    };

    const onMessagesChange = () => {
        if (chatWindowRef.current) {
            chatWindowRef.current.scrollTop =
                chatWindowRef.current.scrollHeight;
        }
    };

    const onAutocompleteChange = () => {
        if (chatWindowRef.current && chatWindowPinnedRef.current) {
            chatWindowRef.current.scrollTop =
                chatWindowRef.current.scrollHeight;
        }
    };

    const handleNewThread = (thread) => {
        setThreadId(thread.id);
        resumeThread(thread.id);
        onNewThread && onNewThread(thread.id);
    };

    const handleSendInput = () => {
        if (pendingFiles.length > 0) {
            onFilesAttached && onFilesAttached(pendingFiles);
        }
        // Immediately block further sends so rapid Enter presses are ignored.
        // (waiting is also set by the SSE callback, but that fires async.)
        startWaiting();
        const textToSend = trimmedInput;
        const filesToSend = pendingFiles;
        setInput("");
        setPendingFiles([]);
        setMentionQuery(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        handleSend(textToSend, filesToSend);
    };

    const handleFileSelect = (event) => {
        const selected = Array.from(event.target.files || []);
        if (selected.length > 0) {
            setPendingFiles((prev) => [...prev, ...selected]);
        }
        event.target.value = "";
    };

    const handleFileRemove = (index) => {
        setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // ─── Drag-and-drop ──────────────────────────────────────────────
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (dragCounterRef.current === 1) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current === 0) setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files || []);
        if (droppedFiles.length > 0) {
            setPendingFiles((prev) => [...prev, ...droppedFiles]);
        }
    };

    // ─── @mention detection ─────────────────────────────────────────
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInput(value);

        if (!enableMentions) return;

        // Detect @ trigger: find the last @ that isn't preceded by a word char
        const cursorPos = e.target.selectionStart || value.length;
        const textBeforeCursor = value.substring(0, cursorPos);
        const atMatch = textBeforeCursor.match(/(^|\s)@(\S*)$/);

        if (atMatch) {
            setMentionQuery(atMatch[2]);
        } else {
            setMentionQuery(null);
        }
    };

    const handleMentionSelect = useCallback(
        (mention) => {
            // Avoid duplicate mentions
            const isDuplicate = mentions.some(
                (m) => m.type === mention.type && m.id === mention.id,
            );
            if (!isDuplicate) {
                setMentions((prev) => [...prev, mention]);
            }

            // Replace the @query text in the input
            const cursorPos = input.length;
            const textBeforeCursor = input.substring(0, cursorPos);
            const atMatch = textBeforeCursor.match(/(^|\s)@(\S*)$/);
            if (atMatch) {
                const beforeAt = textBeforeCursor.substring(
                    0,
                    atMatch.index + atMatch[1].length,
                );
                setInput(beforeAt);
            }

            setMentionQuery(null);
        },
        [mentions, input],
    );

    const handleMentionDismiss = useCallback(() => {
        setMentionQuery(null);
    }, []);

    const handleMentionRemove = (index) => {
        // Prevent removal of default (pinned) mentions
        if (mentions[index]?.isDefault) return;
        setMentions((prev) => prev.filter((_, i) => i !== index));
    };

    const isMentionDropdownOpen = mentionQuery !== null;

    const handleKeyDown = (e) => {
        // If mention dropdown is open, delegate keyboard to it first
        if (isMentionDropdownOpen && mentionDropdownRef.current) {
            mentionDropdownRef.current.handleKeyDown(e);
            if (e.defaultPrevented) return;
        }

        if (!canSend) {
            return;
        }
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendInput();
        }
    };

    const handleScroll = () => {
        const el = chatWindowRef.current;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
        chatWindowPinnedRef.current = atBottom;
    };

    const handleModelMenuOpen = (event) => {
        setModelsAnchorEl(event.currentTarget);
    };

    const handleModelSelect = (name) => {
        setSelectedModel(name);
        setModelsAnchorEl(null);
    };

    const handleModelMenuClose = () => {
        setModelsAnchorEl(null);
    };

    const handlePromptSuggestionSelect = (prompt) => {
        handleSend(prompt);
    };

    const connectThread = (connectThreadId) => {
        request.post(`/threads/${connectThreadId}/connect`).then((response) => {
            const thread = response.data;
            onLoadThread && onLoadThread(thread);
            setThreadId(thread.id);
            setMessages([]);
            setWaiting(false);
            thread.messages.forEach((message) => {
                const component = messageDataToComponent(message);
                addMessage(message.role, component);
            });
            resumeThread(thread.id);
        });
    };

    const newThread = () => {
        request.post(`/threads/connect-empty`).then((_) => {
            setThreadId(null);
            setMessages([]);
        });
    };

    const messageDataToComponent = (data) => {
        return processMessageData(data, handleSend, resumeThread);
    };

    /**
     * Extracts chat_options data from a tool_result message.
     * Returns the options payload if found, null otherwise.
     */
    const extractChatOptions = (data) => {
        if (data.type !== "tool_result") return null;
        try {
            const parsed = JSON.parse(data.content[0]?.content);
            if (parsed?.type === "chat_options")
                return parsed.data.chat_options;
        } catch (e) {
            /* not a chat_options result */
        }
        return null;
    };

    const handleChatOptionSelect = (text) => {
        setPendingChatOptions(null);
        if (waiting) {
            deferredChatOptionRef.current = text;
        } else {
            handleSend(text);
        }
    };

    const handleChatOptionSkip = () => {
        setPendingChatOptions(null);
    };

    const handleUnmount = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    };

    useEffect(() => {
        setMessages([]);
        stopWaiting();
        return handleUnmount;
    }, []);

    useEffect(() => {
        onMessagesChange();
    }, [messages]);

    useEffect(() => {
        if (!newThreadData) {
            return;
        }
        if (newThreadData.type == "reset_thread") {
            newThread();
            return;
        }
        if (newThreadData.type == "elicitation_response") {
            // TODO: implement
            return;
        }
        handleSend(
            newThreadData.input,
            newThreadData.files || [],
            newThreadData.prompt_template || "",
        );
    }, [newThreadData]);

    useEffect(() => {
        if (!defaultThreadId) {
            newThread();
        } else {
            connectThread(defaultThreadId);
        }
        setThreadId(defaultThreadId);
    }, [defaultThreadId]);

    const isEmptyState = messages.length === 0 && !welcomeContent;

    /* ── Shared input bar (used in both empty-state and conversation layouts) ── */
    const inputBar = (
        <Paper
            sx={{
                position: "relative",
                borderRadius: "10px",
                boxShadow: "none",
                flexShrink: 0,
                border: "2px solid transparent",
                transition: "border-color 0.15s ease",
                "&:focus-within": {
                    borderColor: "primary.main",
                },
                ...(isEmptyState ? { width: "100%", maxWidth: 560 } : { m: 1 }),
            }}
        >
            {/* @mention dropdown — floating above the input */}
            {enableMentions && (
                <MentionDropdown
                    ref={mentionDropdownRef}
                    query={mentionQuery}
                    onSelect={handleMentionSelect}
                    onDismiss={handleMentionDismiss}
                />
            )}

            <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* Text input area */}
                <TextFieldBorderless
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                    autoFocus
                    placeholder={
                        enableMentions
                            ? "Describe your project needs\u2026"
                            : "Describe your project needs\u2026"
                    }
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    rows={3}
                />

                {/* Bottom row: file button + chips left, send right */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        px: 0.5,
                        pb: 0.5,
                    }}
                >
                    {/* Left side: file button + chips */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            flex: 1,
                            minWidth: 0,
                            alignItems: "center",
                        }}
                    >
                        <Tooltip title="Attach files">
                            <IconButton
                                size="small"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Paperclip size={20} />
                            </IconButton>
                        </Tooltip>
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            multiple
                            accept={FILE_ACCEPT_FORMATS}
                            onChange={handleFileSelect}
                        />
                        {mentions.map((mention, idx) => (
                            <Chip
                                key={`mention-${mention.type}-${mention.id}`}
                                icon={<Hashtag size={14} />}
                                label={mention.label}
                                size="small"
                                variant="contained"
                                color="secondary"
                                onDelete={
                                    mention.isDefault
                                        ? undefined
                                        : () => handleMentionRemove(idx)
                                }
                                sx={{ maxWidth: 200, p: 0.5, pl: 1 }}
                            />
                        ))}
                        {pendingFiles.map((file, idx) => (
                            <Chip
                                key={`${file.name}-${idx}`}
                                icon={<File size={14} />}
                                label={file.name}
                                size="small"
                                variant="contained"
                                color="secondary"
                                onDelete={() => handleFileRemove(idx)}
                                sx={{ maxWidth: 180, p: 0.5, pl: 1 }}
                            />
                        ))}
                    </Box>

                    {/* Send button */}
                    <IconButton
                        size="small"
                        disabled={!canSend}
                        onClick={handleSendInput}
                    >
                        <RoundArrowUp
                            size={24}
                            color={canSend ? undefined : "#bdbdbd"}
                        />
                    </IconButton>
                </Box>
            </Box>
        </Paper>
    );

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
                position: "relative",
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {isDragging && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0,0,0,0.45)",
                        border: "2px dashed",
                        borderColor: "primary.main",
                        borderRadius: 2,
                        pointerEvents: "none",
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{ color: "#fff", fontWeight: 600 }}
                    >
                        Drop files here
                    </Typography>
                </Box>
            )}

            {isEmptyState ? (
                /* ── Empty state: title + description + input centered ── */
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: "100%",
                            maxWidth: 560,
                            px: 2,
                        }}
                    >
                        <AiLogo />
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 300,
                                mt: 1.5,
                                mb: 0.5,
                                textAlign: "center",
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                opacity: 0.6,
                                mb: 2.5,
                                textAlign: "center",
                                whiteSpace: "pre-line",
                            }}
                        >
                            {description}
                        </Typography>
                        {inputBar}
                    </Box>
                </Box>
            ) : (
                /* ── Conversation state: scrollable messages + input at bottom ── */
                <>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            scrollbarGutter: "stable",
                        }}
                        ref={chatWindowRef}
                        onScroll={handleScroll}
                    >
                        {welcomeContent && (
                            <WelcomeMessage content={welcomeContent} />
                        )}
                        {messages.length > 0 && (
                            <Messages groupName="chat" messages={messages} />
                        )}
                        <DotLoaderMessage waiting={waiting} />
                        <CompletionBubbleMessage />
                        <ToolCompletionBubbleMessage />
                    </Box>

                    {/* Ephemeral chat options card — rendered above the input bar */}
                    {pendingChatOptions && (
                        <ChatOptionsCard
                            title={pendingChatOptions.title}
                            options={pendingChatOptions.options}
                            allowSkip={pendingChatOptions.allow_skip}
                            onSelect={handleChatOptionSelect}
                            onSkip={handleChatOptionSkip}
                        />
                    )}

                    {inputBar}
                </>
            )}
        </div>
    );
}

const Messages = memo(function Messages({ groupName, messages }) {
    const theme = useTheme();
    return (
        <List sx={{ paddingBottom: "36px" }}>
            {messages.map((msg, idx) => {
                const isUser = msg.role === "user";
                if (!msg.content) {
                    return null;
                }
                const dynamicStyles = isUser
                    ? {
                          flex: "0 0 auto",
                          bgcolor: theme.palette.primary.main,
                          borderRadius: "24px",
                          py: "6px",
                          px: "16px",
                      }
                    : {
                          margin: 0,
                          flex: "1 0 auto",
                          bgcolor: "transparent",
                          borderRadius: 0,
                          py: 0,
                          "& p:first-of-type": { marginTop: 0 },
                      };
                return (
                    <ListItem
                        key={idx}
                        sx={{
                            flexDirection: "row",
                            alignItems: "flex-start",
                            justifyContent: isUser ? "flex-end" : "flex-start",
                            padding: isUser ? "12px" : "8px 16px",
                        }}
                    >
                        {!isUser && (
                            <AiLogo
                                additionalStyles={{
                                    width: "18px",
                                    height: "18px",
                                }}
                            />
                        )}
                        <ListItemText
                            primary={msg.content}
                            primaryTypographyProps={{
                                sx: {
                                    fontSize: 14,
                                    textWrap: "auto",
                                    whiteSpace: isUser && "pre-wrap",
                                },
                            }}
                            sx={{
                                ...dynamicStyles,
                                mx: 2,
                                maxWidth: isUser ? "80%" : "95%",
                                display: "inline-block",
                                overflowX: "auto",
                            }}
                        />
                    </ListItem>
                );
            })}
        </List>
    );
});

export const CustomMarkdown = ({ children }) => {
    return (
        <Box
            sx={{
                "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                },
                "& th, & td": {
                    border: "1px solid #ccc",
                    padding: "0.5rem",
                },
                code: {
                    textWrap: "auto",
                },
            }}
        >
            <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                    a(props) {
                        return <Link {...props} target="_blank" />;
                    },
                }}
            >
                {children}
            </Markdown>
        </Box>
    );
};

export const SummaryAndDetails = ({ summaryElement, detailsElement }) => {
    const [open, setOpen] = useState(false);
    const hasDivider = summaryElement && detailsElement;
    return (
        <div>
            {summaryElement}
            <div style={{ fontSize: 12, color: "grey" }}>
                {hasDivider && <Divider sx={{ my: 1 }} />}
                {detailsElement && (
                    <>
                        <span
                            style={{ cursor: "pointer" }}
                            onClick={() => setOpen(!open)}
                        >
                            {`Click to ${open ? "hide" : "show"} details`}
                        </span>
                        <Collapse in={open}>{detailsElement}</Collapse>
                    </>
                )}
            </div>
        </div>
    );
};

export const DotLoaderMessage = ({ waiting }) => {
    const theme = useTheme();
    const completion = useSelector(selectCompletion);
    const toolCompletion = useSelector(selectToolCompletion);
    if (waiting && !completion && !toolCompletion) {
        return (
            <Messages
                groupName="loading"
                messages={[
                    {
                        role: "assistant",
                        content: (
                            <DotLoader
                                darkMode={theme.palette.mode == "dark"}
                            />
                        ),
                    },
                ]}
            />
        );
    }
};

export const CompletionBubbleMessage = () => {
    const completion = useSelector(selectCompletion);
    if (!completion) {
        return null;
    }
    return (
        <Messages
            groupName="completion"
            messages={[
                {
                    role: "assistant",
                    content: (
                        <>
                            {completion && (
                                <CustomMarkdown>{completion}</CustomMarkdown>
                            )}
                        </>
                    ),
                },
            ]}
        />
    );
};

export const ToolCompletionBubbleMessage = () => {
    const toolCompletionData = useSelector(selectToolCompletion);
    const theme = useTheme();
    const [delta, setDelta] = useState("");
    const [snapshot, setSnapshot] = useState({});
    useEffect(() => {
        if (!toolCompletionData) {
            return;
        }
        if (snapshot !== toolCompletionData.snapshot) {
            setDelta("");
            setSnapshot(toolCompletionData.snapshot);
        } else {
            setDelta((delta) => delta + toolCompletionData.partial_json);
        }
    }, [toolCompletionData]);
    if (!toolCompletionData) {
        return null;
    }
    return (
        <Messages
            groupName="toolCompletion"
            messages={[
                {
                    role: "assistant",
                    content: (
                        <SummaryAndDetails
                            summaryElement={
                                <DotLoader
                                    darkMode={theme.palette.mode == "dark"}
                                />
                            }
                            detailsElement={
                                <>
                                    {snapshot && (
                                        <JsonNavigator
                                            data={snapshot}
                                            name={"Data"}
                                            level={0}
                                            md_keys={[]}
                                        />
                                    )}
                                    {delta && <span>{delta}</span>}
                                </>
                            }
                        />
                    ),
                },
            ]}
        />
    );
};

const WelcomeMessage = ({ content }) => {
    const theme = useTheme();
    const { role, projectName, description, details } = content;

    return (
        <Box sx={{ p: 2, display: "flex", gap: 1.5 }}>
            <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                <AiLogo additionalStyles={{ width: "18px", height: "18px" }} />
            </Box>
            <Box
                sx={{
                    flex: 1,
                    fontSize: 13,
                    color: theme.palette.text.secondary,
                    lineHeight: 1.5,
                }}
            >
                <Box
                    sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: theme.palette.text.primary,
                        mb: 0.25,
                    }}
                >
                    {role}
                </Box>
                <span>
                    I'm your <strong>{role}</strong> agent for project{" "}
                    <strong>{projectName}</strong>.
                </span>
                {description && (
                    <Box sx={{ mt: 0.5, fontStyle: "italic", opacity: 0.85 }}>
                        {description}
                    </Box>
                )}
                {details.length > 0 && (
                    <Box sx={{ mt: 0.75, "& p": { margin: 0 } }}>
                        {details.map((detail, idx) => (
                            <CustomMarkdown key={idx}>{detail}</CustomMarkdown>
                        ))}
                    </Box>
                )}
                <Box sx={{ mt: 0.75, opacity: 0.6 }}>
                    How can I help you today?
                </Box>
            </Box>
        </Box>
    );
};

const PromptSuggestions = ({ title, description, suggestions, onPrompt }) => {
    const defaultSuggestions = [
        "What can you do?",
        "Create a project brief",
        "Import a bid",
        "Write a wrap report",
    ];

    const items = Array.isArray(suggestions) ? suggestions : defaultSuggestions;

    return (
        <div
            style={{
                display: "flex",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <div style={{ textAlign: "center", maxWidth: 500 }}>
                <AiLogo />
                <h1 style={{ fontWeight: 100, marginTop: 10, marginBottom: 4 }}>
                    {title}
                </h1>
                {description && (
                    <p
                        style={{
                            opacity: 0.6,
                            fontSize: 14,
                            marginTop: 0,
                            marginBottom: 16,
                            whiteSpace: "pre-line",
                        }}
                    >
                        {description}
                    </p>
                )}
                {items.map((suggestion, index) => (
                    <Button
                        sx={{ margin: 0.5 }}
                        variant="contained"
                        color="secondary"
                        key={index}
                        onClick={() => onPrompt(suggestion)}
                    >
                        {suggestion}
                    </Button>
                ))}
            </div>
        </div>
    );
};
